import { Box, Stack, Typography } from "@mui/joy";
import CheckIcon from "@mui/icons-material/Check";
import { useUserStore, ContactRole } from "shared/store";

interface StepConfig {
  id: number;
  label: string;
  requiredRoles: ContactRole[]; // Roles that can access this step
}

interface OnboardingChevronStepperProps {
  currentStep: 1 | 2 | 3; // 1 = Workday, 2 = Domain, 3 = SSO (original step IDs)
}

// Define steps with their required roles
const allSteps: StepConfig[] = [
  { id: 1, label: "Workday Configuration", requiredRoles: ["workday-admin"] },
  { id: 2, label: "Domain Configuration", requiredRoles: ["it-admin"] },
  { id: 3, label: "SSO Configuration", requiredRoles: ["sso-admin"] }
];

export const OnboardingChevronStepper = ({
  currentStep
}: OnboardingChevronStepperProps) => {
  // Get user roles from the store
  const roles = useUserStore((state) => state.roles);

  // Filter steps based on user roles
  // If roles array is empty (legacy users or no roles set), show all steps
  const steps =
    roles.length > 0
      ? allSteps.filter((step) =>
          step.requiredRoles.some((role) => roles.includes(role))
        )
      : allSteps;
  // Get onboarding data from sessionStorage to check what's been completed
  const getOnboardingData = () => {
    try {
      const data = sessionStorage.getItem("onboardingData");
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  };

  const onboardingData = getOnboardingData();

  // Check which steps have been completed based on saved data
  const isWorkdayCompleted = !!onboardingData.workday;
  const isDomainCompleted = !!onboardingData.domains;
  const isSsoCompleted = !!(
    onboardingData.sso_configs || onboardingData.sso_provider
  );

  const isStepCompleted = (stepId: number) => {
    if (stepId === 1) return isWorkdayCompleted;
    if (stepId === 2) return isDomainCompleted;
    if (stepId === 3) return isSsoCompleted;
    return false;
  };

  const getStepStatus = (stepId: number) => {
    // If it's the current step, return "current" (but we'll still check completion for the icon)
    if (stepId === currentStep) {
      return "current";
    }

    // For other steps, check if they're completed
    if (isStepCompleted(stepId)) {
      return "completed";
    }

    return "upcoming";
  };

  const getStepColors = (status: string) => {
    switch (status) {
      case "completed":
        return {
          bg: "success.100",
          border: "success.500",
          text: "success.700"
        };
      case "current":
        return {
          bg: "primary.100",
          border: "primary.500",
          text: "primary.700"
        };
      case "upcoming":
        return {
          bg: "neutral.50",
          border: "neutral.300",
          text: "neutral.500"
        };
      default:
        return {
          bg: "neutral.50",
          border: "neutral.300",
          text: "neutral.500"
        };
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        pt: 3,
        pb: 2,
        px: 2
      }}
    >
      <Box
        sx={{
          maxWidth: "500px",
          mx: "auto"
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="center">
          {steps.map((step, index) => {
            const status = getStepStatus(step.id);
            const colors = getStepColors(status);
            const isLast = index === steps.length - 1;
            // Display sequential number (1-based) instead of original step ID
            const displayNumber = index + 1;

            return (
              <Stack
                key={step.id}
                direction="row"
                alignItems="center"
                sx={{ flex: isLast ? 0 : 1 }}
              >
                {/* Step Circle */}
                <Stack alignItems="center" spacing={0.25}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor:
                        status === "completed"
                          ? colors.border
                          : status === "current"
                            ? colors.border
                            : "background.level1",
                      border: "1.5px solid",
                      borderColor: colors.border,
                      color: status === "upcoming" ? colors.text : "white",
                      fontWeight: "bold",
                      fontSize: "11px",
                      transition: "all 0.3s ease"
                    }}
                  >
                    {isStepCompleted(step.id) ? (
                      <CheckIcon sx={{ fontSize: 14 }} />
                    ) : (
                      displayNumber
                    )}
                  </Box>
                  <Typography
                    level="body-xs"
                    sx={{
                      color: colors.text,
                      fontWeight: status === "current" ? 600 : 500,
                      textAlign: "center",
                      maxWidth: "100px",
                      fontSize: "0.65rem",
                      lineHeight: 1.2,
                      display: {
                        xs: "none",
                        md: "block"
                      }
                    }}
                  >
                    {step.label}
                  </Typography>
                </Stack>

                {/* Connector Line */}
                {!isLast && (
                  <Box
                    sx={{
                      flex: 1,
                      height: "1.5px",
                      backgroundColor: isStepCompleted(step.id)
                        ? "success.500"
                        : "neutral.300",
                      mx: 1,
                      transition: "all 0.3s ease"
                    }}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>

        {/* Mobile: Show current step label */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            mt: 0.5,
            textAlign: "center"
          }}
        >
          {steps.map((step) => {
            const status = getStepStatus(step.id);
            const colors = getStepColors(status);

            if (status === "current") {
              return (
                <Typography
                  key={step.id}
                  level="body-xs"
                  sx={{
                    color: colors.text,
                    fontWeight: 600,
                    fontSize: "0.65rem"
                  }}
                >
                  {step.label}
                </Typography>
              );
            }
            return null;
          })}
        </Box>
      </Box>
    </Box>
  );
};
